(() => {
  if (customElements.get('pdp-bundle-item')) return;

  class PDPBundleItem extends HTMLElement {
    constructor() {
      super();
      this.onSelectChange = this.onSelectChange.bind(this);
      this.onAvailabilityChange = this.onAvailabilityChange.bind(this);
      this._ready = false;
      this._variants = [];
      this._cartVariantQuantities = {};
    }

    connectedCallback() {
      this.hiddenInput = this.querySelector('.js-pdp-bundle-variant-id');
      this.imageEl = this.querySelector('.js-pdp-bundle-item-image');
      if (this.imageEl instanceof HTMLImageElement) {
        this._imageDefaultSrc = this.imageEl.getAttribute('src') || '';
      }

      this._variants = this.loadVariantsFromDom();
      window.DOBundleAvailability?.registerVariants(this._variants);
      window.addEventListener('bundle:availability:change', this.onAvailabilityChange);
      this.bootstrap();
      this.syncCartAvailability();
    }

    disconnectedCallback() {
      this.querySelectorAll('.js-pdp-bundle-variant-option').forEach((sel) => {
        sel.removeEventListener('change', this.onSelectChange);
      });
      window.removeEventListener('bundle:availability:change', this.onAvailabilityChange);
    }

    parseJson(value, fallback) {
      if (!value?.trim()) return fallback;
      try {
        return JSON.parse(value);
      } catch {
        return fallback;
      }
    }

    loadVariantInventoryFromDom() {
      const script = this.querySelector('.js-pdp-bundle-inventory-json');
      return this.parseJson(this.dataset.variantInventoryJson || script?.textContent || '', {});
    }

    loadVariantsFromDom() {
      const script = this.querySelector('.js-pdp-bundle-variants-json');
      const parsed = this.parseJson(this.dataset.variantsJson || script?.textContent || '', []);
      const inventoryById = this.loadVariantInventoryFromDom();

      return this.normalizeVariants(
        (Array.isArray(parsed) ? parsed : []).map((variant) => ({
          ...variant,
          ...(inventoryById[String(variant.id)] || {}),
        }))
      );
    }

    normalizeOptionValue(value) {
      return String(value ?? '').trim();
    }

    getOptionIndex(selectEl) {
      const position = Number(selectEl.dataset.optionPosition);
      return Number.isFinite(position) && position > 0 ? position - 1 : 0;
    }

    getVariantOptionValues(variant) {
      if (!variant) return [];
      const fromKeys = [variant.option1, variant.option2, variant.option3]
        .filter((opt) => opt != null && opt !== '')
        .map((opt) => this.normalizeOptionValue(opt));
      if (fromKeys.length) return fromKeys;
      if (Array.isArray(variant.options) && variant.options.length) {
        return variant.options.map((opt) => this.normalizeOptionValue(opt));
      }
      return [];
    }

    normalizeVariants(rawVariants) {
      return rawVariants.map((variant) => ({
        ...variant,
        options: this.getVariantOptionValues(variant),
        available: Boolean(variant.available),
        inventory_quantity:
          typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : Number(variant.inventory_quantity),
      }));
    }

    getVariantInventoryLimit(variant) {
      if (!variant?.inventory_management) return null;
      if (variant.inventory_policy === 'continue') return null;

      const quantity = Number(variant.inventory_quantity);
      return Number.isFinite(quantity) ? Math.max(0, quantity) : null;
    }

    getCartVariantQuantity(variantId) {
      return Number(this._cartVariantQuantities[String(variantId)] || 0);
    }

    isVariantAtCartLimit(variant) {
      const limit = this.getVariantInventoryLimit(variant);
      if (limit == null) return false;
      return this.getCartVariantQuantity(variant.id) >= limit;
    }

    isVariantPurchasable(variant) {
      return Boolean(variant?.available) && !this.isVariantAtCartLimit(variant);
    }

    hasSingleVariant() {
      return this.dataset.hasSingleVariant === 'true' || this._variants.length === 1;
    }

    hasPurchasableVariant() {
      return this._variants.some((variant) => this.isVariantPurchasable(variant));
    }

    getSelects() {
      return Array.from(this.querySelectorAll('.js-pdp-bundle-variant-option')).sort(
        (a, b) => this.getOptionIndex(a) - this.getOptionIndex(b)
      );
    }

    getSelectedOptions() {
      const selects = this.getSelects();
      if (!selects.length) return [];

      const maxIdx = selects.reduce((max, sel) => Math.max(max, this.getOptionIndex(sel)), 0);
      const options = Array(maxIdx + 1).fill(null);

      selects.forEach((sel) => {
        const idx = this.getOptionIndex(sel);
        const raw = sel.value;
        options[idx] = raw === '' || raw == null ? null : this.normalizeOptionValue(raw);
      });

      return options;
    }

    getMatchingVariants(testOptions) {
      return this._variants.filter((variant) => {
        const opts = variant.options || [];
        return testOptions.every((opt, i) => {
          if (opt === null || opt === '') return true;
          if (i >= opts.length) return false;
          return opts[i] === opt;
        });
      });
    }

    isOptionValueSoldOut(optionIdx, value, selectedOptions) {
      const testOptions = selectedOptions.map((opt, idx) => (idx < optionIdx ? opt : null));
      testOptions[optionIdx] = this.normalizeOptionValue(value);
      const matching = this.getMatchingVariants(testOptions);
      if (matching.length === 0) return true;
      return matching.every((v) => !this.isVariantPurchasable(v));
    }

    findVariantByOptions(selectedOptions) {
      if (!selectedOptions.every((x) => x != null && x !== '')) return null;
      return (
        this._variants.find((variant) => {
          const opts = variant.options || [];
          return opts.length === selectedOptions.length && opts.every((opt, idx) => opt === selectedOptions[idx]);
        }) || null
      );
    }

    resetVariantSelectsOnly() {
      this.getSelects().forEach((sel) => {
        const placeholder = Array.from(sel.options).find((opt) => opt.value === '');
        if (placeholder) {
          sel.value = '';
          sel.selectedIndex = 0;
          return;
        }
        const firstAvailable = Array.from(sel.options).find((opt) => opt.value !== '' && !opt.disabled);
        if (firstAvailable) {
          sel.value = firstAvailable.value;
        }
      });
      if (this.hiddenInput) this.hiddenInput.value = '';
      this.resetImage();
      this._ready = false;
      this.updateOptionAvailability();
      this.notifyChange();
    }

    selectOnlyValueOptions() {
      let changed = false;

      this.getSelects().forEach((sel) => {
        if (sel.value !== '') return;

        const realOptions = Array.from(sel.options).filter((opt) => opt.value !== '');
        if (realOptions.length !== 1 || realOptions[0].disabled) return;

        sel.value = realOptions[0].value;
        changed = true;
      });

      return changed;
    }

    resetUnavailableSelectedOption(selectedOptions = this.getSelectedOptions()) {
      if (!this.hasPurchasableVariant()) {
        this.resetVariantSelectsOnly();
        return true;
      }

      const selects = this.getSelects();
      const target = selects.find((sel) => {
        const optionIdx = this.getOptionIndex(sel);
        const value = selectedOptions[optionIdx];
        if (value == null || value === '') return false;
        return this.isOptionValueSoldOut(optionIdx, value, selectedOptions);
      });

      if (!target) return false;

      target.value = '';
      target.selectedIndex = 0;
      if (this.hiddenInput) this.hiddenInput.value = '';
      this._ready = false;
      this.syncFromSelects();
      return true;
    }

    resetImage() {
      if (!this.imageEl || !this._imageDefaultSrc) return;
      this.imageEl.setAttribute('src', this._imageDefaultSrc);
      this.imageEl.removeAttribute('srcset');
    }

    updateOptionAvailability() {
      const selects = this.getSelects();
      if (!selects.length) return;

      const selectedOptions = this.getSelectedOptions();

      if (!this._variants.length) {
        selects.forEach((sel) => {
          Array.from(sel.options).forEach((opt) => {
            if (opt.value === '') {
              opt.disabled = false;
              return;
            }
            opt.disabled = opt.dataset.purchasable === 'false';
          });
        });
        return;
      }

      selects.forEach((sel) => {
        const optionIdx = this.getOptionIndex(sel);

        Array.from(sel.options).forEach((opt) => {
          if (opt.value === '') {
            opt.disabled = false;
            return;
          }

          opt.disabled = this.isOptionValueSoldOut(optionIdx, opt.value, selectedOptions);
        });
      });
    }

    setCartVariantQuantities(variantQuantities) {
      this._cartVariantQuantities = {};

      if (variantQuantities && typeof variantQuantities === 'object') {
        Object.entries(variantQuantities).forEach(([variantId, quantity]) => {
          const numericQuantity = Number(quantity);
          this._cartVariantQuantities[String(variantId)] = Number.isFinite(numericQuantity) ? numericQuantity : 0;
        });
      }

      this.syncFromSelects();
    }

    syncCartAvailability() {
      const quantities = window.DOBundleAvailability?.getReservedQuantities?.() || {};
      this.setCartVariantQuantities(quantities);
    }

    onAvailabilityChange(event) {
      this.setCartVariantQuantities(event.detail?.reservedQuantities || {});
    }

    syncFromSelects() {
      const selects = this.getSelects();

      if (!selects.length || this.hasSingleVariant()) {
        const variant = this._variants[0];
        const purchasable = this.isVariantPurchasable(variant);
        if (this.hiddenInput) this.hiddenInput.value = purchasable ? String(variant.id) : '';
        this._ready = purchasable;
        if (purchasable) this.updateImage(variant);
        else this.resetImage();
        this.notifyChange();
        return;
      }

      if (!this._variants.length) {
        this._ready = false;
        this.notifyChange();
        return;
      }

      this.updateOptionAvailability();
      if (this.selectOnlyValueOptions()) {
        this.updateOptionAvailability();
      }

      if (!this.hasPurchasableVariant()) {
        this.resetVariantSelectsOnly();
        return;
      }

      const selectedOptions = this.getSelectedOptions();
      const allChosen = selectedOptions.length > 0 && selectedOptions.every((x) => x != null && x !== '');
      const variant = allChosen ? this.findVariantByOptions(selectedOptions) : null;

      if (allChosen && !this.isVariantPurchasable(variant)) {
        this.resetUnavailableSelectedOption(selectedOptions);
        return;
      }

      const hasPartial = selectedOptions.some((x) => x != null && x !== '');
      if (hasPartial) {
        const preview = this._variants.find(
          (v) =>
            selectedOptions.every((selOpt, idx) => selOpt == null || selOpt === '' || v.options[idx] === selOpt) &&
            this.isVariantPurchasable(v)
        );
        if (preview) this.updateImage(preview);
        else if (!allChosen) this.resetImage();
      } else {
        this.resetImage();
      }

      if (this.hiddenInput) {
        this.hiddenInput.value = this.isVariantPurchasable(variant) ? String(variant.id) : '';
      }

      this._ready = this.isVariantPurchasable(variant);
      this.notifyChange();
    }

    updateImage(variant) {
      if (!this.imageEl) return;
      const src = variant?.featured_image?.src || variant?.featured_image?.url;
      if (!src) return;
      this.imageEl.setAttribute('src', src);
      this.imageEl.removeAttribute('srcset');
    }

    onSelectChange() {
      this.syncFromSelects();
    }

    notifyChange() {
      this.dispatchEvent(
        new CustomEvent('bundle:change', {
          bubbles: true,
          detail: { ready: this._ready, item: this },
        })
      );
    }

    async refreshVariants() {
      const handle = this.dataset.productHandle?.trim();
      if (!handle) return;

      const root = window.Shopify?.routes?.root ?? '/';
      try {
        const currentVariantsById = new Map(this._variants.map((variant) => [String(variant.id), variant]));
        const response = await fetch(`${root}products/${handle}.js`, {
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (!response.ok) return;
        const product = await response.json();
        this._variants = this.normalizeVariants(product.variants || []).map((variant) => {
          const currentVariant = currentVariantsById.get(String(variant.id));
          return {
            ...variant,
            inventory_quantity:
              Number.isFinite(variant.inventory_quantity) || !currentVariant
                ? variant.inventory_quantity
                : currentVariant.inventory_quantity,
            inventory_policy: variant.inventory_policy ?? currentVariant?.inventory_policy,
            inventory_management: variant.inventory_management ?? currentVariant?.inventory_management,
          };
        });
        window.DOBundleAvailability?.registerVariants(this._variants);
        const script = this.querySelector('.js-pdp-bundle-variants-json');
        if (script) script.textContent = JSON.stringify(this._variants);
        this.updateOptionAvailability();
      } catch {
        /* keep cached */
      }
    }

    preserveSelections() {
      this._preservedOptions = this.getSelectedOptions();
      const variant = this.getResolvedVariant();
      this._preservedVariantId = variant?.id ? String(variant.id) : '';
    }

    restoreSelections() {
      const preserved = this._preservedOptions;
      if (!Array.isArray(preserved)) return;

      this.getSelects().forEach((sel) => {
        const idx = this.getOptionIndex(sel);
        const val = preserved[idx];
        if (val == null || val === '') {
          const placeholder = Array.from(sel.options).find((opt) => opt.value === '');
          if (placeholder) {
            sel.value = '';
            sel.selectedIndex = 0;
          } else {
            const firstAvailable = Array.from(sel.options).find((opt) => opt.value !== '' && !opt.disabled);
            if (firstAvailable) sel.value = firstAvailable.value;
          }
          return;
        }
        sel.value = val;
        if (sel.value !== val) {
          for (let i = 0; i < sel.options.length; i++) {
            if (this.normalizeOptionValue(sel.options[i].value) === val) {
              sel.selectedIndex = i;
              break;
            }
          }
        }
      });
    }

    preservedVariantIsUnavailable() {
      if (!this._preservedVariantId || !Array.isArray(this._preservedOptions)) return false;
      const allChosen = this._preservedOptions.every((x) => x != null && x !== '');
      if (!allChosen) return false;
      const variant = this._variants.find((v) => String(v.id) === String(this._preservedVariantId));
      if (!variant) return false;
      return !this.isVariantPurchasable(variant);
    }

    reconcileAfterCart() {
      if (!Array.isArray(this._preservedOptions)) {
        this.syncFromSelects();
        return;
      }

      if (this.preservedVariantIsUnavailable()) {
        this.resetUnavailableSelectedOption(this._preservedOptions);
        return;
      }

      this.restoreSelections();
      this.syncFromSelects();
    }

    bootstrap() {
      this.updateOptionAvailability();

      this.getSelects().forEach((sel) => {
        sel.addEventListener('change', this.onSelectChange);
      });

      this.syncFromSelects();
    }

    isReady() {
      return this._ready;
    }

    getResolvedVariant() {
      const selects = this.getSelects();
      if (!selects.length) return this._variants[0] || null;
      return this.findVariantByOptions(this.getSelectedOptions());
    }
  }

  customElements.define('pdp-bundle-item', PDPBundleItem);
})();
